import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import InspectWindow from "./ItemInspectWindow.jsx";
import Spinner from "./Spinner.jsx";
import { fetchWikiImageForItem } from "../services/gw2wiki/gw2WikiService";

const rarityColors = {
  Basic: "#fff",
  Fine: "#4fc3f7",
  Masterwork: "#43a047",
  Rare: "#fbc02d",
  Exotic: "#ff9800",
  Ascended: "#e040fb",
  Legendary: "#8e24aa",
};

function getBestItemLink(item) {
  const link = item?.chat_link || item?.name || "";
  if (item?.count > 1 && link) {
    return `${link}(${item.count})`;
  }
  return link;
}

function getPreviewImage(item, wikiThumbs) {
  if (!item) return null;
  return (
    (item.image_url && String(item.image_url).trim()) ||
    (wikiThumbs[item.id] && String(wikiThumbs[item.id]).trim()) ||
    null
  );
}

export default function WinnersPanel({
  winners,
  nameSuggestions,
  assigningWinnerId,
  onAddWinner,
  onStartAssignItem,
  onCancelAssignItem,
  onRemoveWinner,
  onRemoveWinnerItem,
  disabled,
}) {
  const [nameInput, setNameInput] = useState("");
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [suggestionsRect, setSuggestionsRect] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const [inspectedItem, setInspectedItem] = useState(null);
  const [wikiThumbs, setWikiThumbs] = useState({});
  const [wikiLoading, setWikiLoading] = useState({});
  const addFormRef = useRef(null);
  const addInputRef = useRef(null);
  const addInputWrapRef = useRef(null);
  const suggestionsRef = useRef(null);
  const winnersStripRef = useRef(null);

  const uniqueSuggestions = useMemo(
    () => Array.from(new Set(nameSuggestions.map((name) => name.trim()).filter(Boolean))),
    [nameSuggestions],
  );

  const visibleSuggestions = useMemo(() => {
    const query = nameInput.trim().toLowerCase();
    const unmatchedNames = uniqueSuggestions.filter(
      (name) => !winners.some((winner) => winner.name.trim().toLowerCase() === name.toLowerCase()),
    );

    if (!query) {
      return unmatchedNames.slice(0, 6);
    }

    return unmatchedNames
      .filter((name) => name.toLowerCase().includes(query))
      .slice(0, 6);
  }, [nameInput, uniqueSuggestions, winners]);

  const assignedItems = useMemo(
    () => winners.map((winner) => winner.item).filter(Boolean),
    [winners],
  );

  const inspectedIndex = inspectedItem
    ? assignedItems.findIndex((item) => item.id === inspectedItem.id)
    : -1;
  const hasCircularInspectNavigation = assignedItems.length > 1 && inspectedIndex !== -1;

  useEffect(() => {
    const handleClickAway = (event) => {
      if (addFormRef.current?.contains(event.target)) return;
      if (suggestionsRef.current?.contains(event.target)) return;
      setIsSuggestionsOpen(false);
    };

    document.addEventListener("mousedown", handleClickAway);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  }, []);

  useEffect(() => {
    if (!isSuggestionsOpen || !addInputWrapRef.current) return undefined;

    const updateRect = () => {
      const rect = addInputWrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      setSuggestionsRect({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        viewportHeight: window.innerHeight,
      });
    };

    updateRect();

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [isSuggestionsOpen]);

  useEffect(() => {
    const wikiTarget = hoveredItem || inspectedItem;
    if (!wikiTarget || !wikiTarget.name) return undefined;
    if (wikiTarget.image_url || wikiThumbs[wikiTarget.id]) return undefined;

    let cancelled = false;
    setWikiLoading((prev) => ({ ...prev, [wikiTarget.id]: true }));

    (async () => {
      try {
        const src = await fetchWikiImageForItem(wikiTarget);
        if (cancelled) return;
        if (src) {
          setWikiThumbs((prev) => ({ ...prev, [wikiTarget.id]: src }));
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Unable to load wiki image for winner item.", error);
        }
      } finally {
        if (!cancelled) {
          setWikiLoading((prev) => ({ ...prev, [wikiTarget.id]: false }));
        }
      }
    })();

    return () => {
      cancelled = true;
      setWikiLoading((prev) => ({ ...prev, [wikiTarget.id]: false }));
    };
  }, [hoveredItem, inspectedItem, wikiThumbs]);

  useEffect(() => {
    const strip = winnersStripRef.current;
    if (!strip) return undefined;

    const scrollToRight = () => {
      strip.scrollLeft = strip.scrollWidth;
    };

    scrollToRight();

    const frameId = requestAnimationFrame(scrollToRight);
    return () => cancelAnimationFrame(frameId);
  }, [winners.length]);

  const inspectedImageSrc = inspectedItem
    ? getPreviewImage(inspectedItem, wikiThumbs) || inspectedItem.icon || null
    : null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    onAddWinner(trimmed);
    setNameInput("");
    setIsSuggestionsOpen(false);
  };

  const openAssignMenu = (winnerId) => {
    if (disabled) return;
    if (assigningWinnerId === winnerId) {
      onCancelAssignItem?.();
      return;
    }
    onStartAssignItem?.(winnerId);
  };

  const handleHoverEnter = (item, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredItem(item);
    setHoveredRect({
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
  };

  const handleHoverLeave = () => {
    setHoveredItem(null);
    setHoveredRect(null);
  };

  const handleInspectPrev = () => {
    if (!hasCircularInspectNavigation) return;
    const prevIndex = inspectedIndex === 0 ? assignedItems.length - 1 : inspectedIndex - 1;
    setInspectedItem(assignedItems[prevIndex]);
  };

  const handleInspectNext = () => {
    if (!hasCircularInspectNavigation) return;
    const nextIndex = inspectedIndex === assignedItems.length - 1 ? 0 : inspectedIndex + 1;
    setInspectedItem(assignedItems[nextIndex]);
  };

  const handleInspectCopyLink = () => {
    if (!inspectedItem) return;
    const link = getBestItemLink(inspectedItem);
    if (!link) return;
    navigator.clipboard.writeText(link);
  };

  const handleSuggestionClick = (name) => {
    onAddWinner(name);
    setNameInput("");
    setIsSuggestionsOpen(false);
    addInputRef.current?.blur();
  };

  return (
    <div className="winners-panel">
      <div className="winners-inline-row">
        <div className="winners-inline-title">Winners</div>

        <div ref={winnersStripRef} className="winners-strip">
          {winners.length === 0 ? (
            <p>No winners added yet.</p>
          ) : (
            winners.map((winner) => {
            const assignedItem = winner.item;
            const isAssigningThisWinner = assigningWinnerId === winner.id;
            return (
              <div key={winner.id} className="winner-card">
                <button
                  type="button"
                  className="winner-remove-btn"
                  aria-label={`Remove ${winner.name} from winners`}
                  onClick={() => onRemoveWinner(winner.id)}
                >
                  ×
                </button>

                <div className="winner-card-name" title={winner.name}>
                  {winner.name}
                </div>

                <div className="winner-card-slot-wrap">
                  {assignedItem ? (
                    <div className="winner-item-slot-shell">
                      <button
                        type="button"
                        className="winner-item-clear-btn"
                        aria-label={`Remove ${assignedItem.name} from ${winner.name}`}
                        onClick={() => onRemoveWinnerItem(winner.id)}
                      >
                        ×
                      </button>

                      <button
                        type="button"
                        className={`winner-item-slot winner-item-slot-filled ${isAssigningThisWinner ? "winner-item-slot-active" : ""}`}
                        onMouseEnter={(event) => handleHoverEnter(assignedItem, event)}
                        onMouseLeave={handleHoverLeave}
                        onClick={() => openAssignMenu(winner.id)}
                        onContextMenu={(event) => {
                          event.preventDefault();
                          setInspectedItem(assignedItem);
                        }}
                        disabled={disabled}
                        title={isAssigningThisWinner ? "Cancel item selection" : "Click to change item"}
                      >
                        <img src={assignedItem.icon} alt={assignedItem.name} className="winner-item-image" />
                        {assignedItem.count > 1 && (
                          <span className="winner-item-count">{assignedItem.count}</span>
                        )}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={`winner-item-slot winner-item-slot-empty ${isAssigningThisWinner ? "winner-item-slot-active" : ""}`}
                      onClick={() => openAssignMenu(winner.id)}
                      disabled={disabled}
                    >
                      {isAssigningThisWinner ? "Pick" : "+"}
                    </button>
                  )}
                  {isAssigningThisWinner && (
                    <div className="winner-assign-inline-hint">Pick from Items</div>
                  )}
                </div>
              </div>
            );
          })
          )}

          <form ref={addFormRef} className="winners-inline-add" onSubmit={handleSubmit}>
            <div ref={addInputWrapRef} className="winners-inline-add-input-wrap">
              <input
                ref={addInputRef}
                type="text"
                value={nameInput}
                onChange={(event) => {
                  setNameInput(event.target.value);
                  setIsSuggestionsOpen(true);
                }}
                onFocus={() => setIsSuggestionsOpen(true)}
                placeholder="Enter a name"
                className="login-input winners-add-input"
                disabled={disabled}
              />
            </div>
            <button type="submit" className="names-btn primary winners-add-btn" disabled={disabled || !nameInput.trim()}>
              Add
            </button>
          </form>
        </div>
      </div>

      {isSuggestionsOpen && visibleSuggestions.length > 0 && suggestionsRect &&
        createPortal(
          <div
            ref={suggestionsRef}
            className="winners-suggestions-list"
            style={{
              position: "fixed",
              left: suggestionsRect.left,
              bottom: suggestionsRect.viewportHeight - suggestionsRect.top + 6,
              width: suggestionsRect.width,
              maxHeight: Math.max(56, suggestionsRect.top - 16),
            }}
          >
            {visibleSuggestions.map((name) => (
              <button
                key={name}
                type="button"
                className="winners-suggestion-btn"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSuggestionClick(name)}
              >
                {name}
              </button>
            ))}
          </div>,
          document.body,
        )}

      {hoveredItem && hoveredRect &&
        createPortal(
          <div
            className="winner-hover-card"
            style={{
              left: Math.max(12, Math.min(hoveredRect.right + 8, hoveredRect.viewportWidth - 176)),
              bottom: hoveredRect.viewportHeight - hoveredRect.top + 8,
              maxHeight: Math.max(120, hoveredRect.top - 12),
            }}
          >
            <div className="winner-hover-title">{hoveredItem.name}</div>
            {getPreviewImage(hoveredItem, wikiThumbs) ? (
              <img
                src={getPreviewImage(hoveredItem, wikiThumbs)}
                alt={hoveredItem.name}
                className="winner-hover-image"
                style={{ borderColor: rarityColors[hoveredItem.rarity] || "#234" }}
              />
            ) : wikiLoading[hoveredItem.id] ? (
              <div className="winner-hover-loading">
                <Spinner />
                <div>Loading image</div>
              </div>
            ) : null}
          </div>,
          document.body,
        )}

      <InspectWindow
        visible={!!inspectedItem}
        title={inspectedItem ? inspectedItem.name : ""}
        imageSrc={inspectedImageSrc}
        isImageLoading={!!(inspectedItem && wikiLoading[inspectedItem.id] && !inspectedImageSrc)}
        accentColor={inspectedItem ? rarityColors[inspectedItem.rarity] || "#234" : "#234"}
        fallbackText="No preview image available for this item."
        onClose={() => setInspectedItem(null)}
        onCopy={inspectedItem ? handleInspectCopyLink : undefined}
        onPrev={hasCircularInspectNavigation ? handleInspectPrev : undefined}
        onNext={hasCircularInspectNavigation ? handleInspectNext : undefined}
      />
    </div>
  );
}